import React, { useState } from 'react';

import Button from 'components/Button';
import LocalizedLink from 'components/LocalizedLink';
import { useI18n } from 'i18n/I18nContext';
import trackEvent from 'utils/trackEvent';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const ChecklistLeadForm = () => {
  const { t, locale } = useI18n();
  const [status, setStatus] = useState<FormStatus>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get('email') || '').trim();
    const business = String(data.get('business') || '').trim();
    const website = String(data.get('website') || '');

    if (!email) {
      setEmailError(t('checklist.formEmailRequired'));
      trackEvent('checklist_lead_validation', { reason: 'empty', locale });
      const emailInput = form.querySelector<HTMLInputElement>('#checklist-email');
      emailInput?.focus();
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError(t('checklist.formEmailInvalid'));
      trackEvent('checklist_lead_validation', { reason: 'invalid', locale });
      const emailInput = form.querySelector<HTMLInputElement>('#checklist-email');
      emailInput?.focus();
      return;
    }

    setEmailError(null);
    setStatus('submitting');
    try {
      const res = await fetch('/api/email/checklist-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          business,
          locale,
          website,
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        throw new Error(payload.error || `Request failed: ${res.status}`);
      }
      setStatus('success');
      form.reset();
      trackEvent('checklist_lead_success', { locale, hasBusiness: Boolean(business) });
    } catch {
      setStatus('error');
      trackEvent('checklist_lead_error', { locale });
    }
  };

  if (status === 'success') {
    return (
      <div className="gb-lead-form gb-lead-form--success" role="status">
        <p className="m-0 font-bold">{t('checklist.formSuccessTitle')}</p>
        <p className="mt-10 mb-0 gb-prose-muted">{t('checklist.formSuccessBody')}</p>
      </div>
    );
  }

  return (
    <form className="gb-lead-form" onSubmit={handleSubmit} noValidate>
      {/* Spam honeypot: off-screen, not announced, not tabbable */}
      <div className="gb-honeypot" aria-hidden="true">
        <input name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
      </div>

      <p className="mb-25 gb-prose-muted">{t('checklist.formIntro')}</p>

      <div className="mb-20 flex flex-col">
        <label htmlFor="checklist-email" className="text-sm font-normal mb-5">
          {t('checklist.formEmail')}
        </label>
        <input
          id="checklist-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          className="gb-lead-input"
          disabled={status === 'submitting'}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? 'checklist-email-error' : undefined}
          onChange={() => {
            if (emailError) setEmailError(null);
          }}
        />
        {emailError ? (
          <p id="checklist-email-error" className="text-red text-sm mt-10 mb-0" role="alert">
            {emailError}
          </p>
        ) : null}
      </div>

      <div className="mb-25 flex flex-col">
        <label htmlFor="checklist-business" className="text-sm font-normal mb-5">
          {t('checklist.formBusiness')}
        </label>
        <input
          id="checklist-business"
          name="business"
          type="text"
          autoComplete="organization"
          className="gb-lead-input"
          disabled={status === 'submitting'}
        />
      </div>

      {status === 'error' ? (
        <div className="text-red text-sm mb-20" role="alert">
          {t('checklist.formError')}
        </div>
      ) : null}

      <Button
        type="submit"
        disabled={status === 'submitting'}
        value={status === 'submitting' ? t('checklist.formSubmitting') : t('checklist.formSubmit')}
      />
      <p className="mt-15 mb-0 text-xs gb-prose-muted">
        {t('checklist.formPrivacyBefore')}{' '}
        <LocalizedLink className="text-green font-bold" to="/privacy">
          {t('footer.privacy')}
        </LocalizedLink>
        {t('checklist.formPrivacyAfter')}
      </p>
    </form>
  );
};

export default ChecklistLeadForm;



// WEBPACK FOOTER //
// ./src/components/ChecklistLeadForm.tsx